import { type FormEvent, useEffect, useRef, useState } from 'react'
import { CheckCircle } from 'lucide-react'
import SignaturePad from 'signature_pad'
import { useAuth } from '../../context/AuthContext'
import {
  changePassword,
  deleteSignature,
  myApplications,
  saveSignature,
  updateProfile,
  type StaffApplication,
} from '../../api/auth'
import { useToast } from '../../components/ui/Toast'
import { useConfirm } from '../../components/ui/ConfirmModal'
import { Card, SectionTitle, ErrorBox } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { inputClass } from '../../components/ui/FormField'
import ApplyForStaff from './ApplyForStaff'

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  org_admin: 'Org Admin',
  staff: 'Staff',
  client: 'Client',
  user: 'User',
}

const CLINICAL_ROLE_LABELS: Record<string, string> = {
  veterinarian: 'Veterinarian',
  vet_technician: 'Veterinary Technician',
  receptionist: 'Receptionist',
  non_clinical: 'Non-Clinical',
}

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-success-bg text-success-text border-success-border',
  invited: 'bg-accent-muted text-accent border-accent-muted',
  pending_approval: 'bg-warning-bg text-warning-text border-warning-border',
  rejected: 'bg-danger-bg text-danger-text border-danger-border',
  revoked: 'bg-bg text-text-3 border-border',
  expired: 'bg-bg text-text-3 border-border',
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  invited: 'Invited',
  pending_approval: 'Pending approval',
  rejected: 'Rejected',
  revoked: 'Revoked',
  expired: 'Expired',
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

// ── Account section ───────────────────────────────────────────────────────────

function AccountCard() {
  const { user, refreshUser } = useAuth()
  const toast = useToast()

  const [editing, setEditing] = useState(false)
  const [fullName, setFullName] = useState(user?.full_name ?? '')
  const [jobTitle, setJobTitle] = useState(user?.job_title ?? '')
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startEdit = () => {
    setFullName(user?.full_name ?? '')
    setJobTitle(user?.job_title ?? '')
    setPhoneNumber(user?.phone_number ?? '')
    setError(null)
    setEditing(true)
  }

  const handleSave = async (e: FormEvent) => {
    e.preventDefault()
    if (!fullName.trim()) { setError('Full name is required'); return }
    setSaving(true)
    setError(null)
    try {
      await updateProfile({ full_name: fullName, job_title: jobTitle || null, phone_number: phoneNumber || null })
      toast.success('Profile updated')
      setEditing(false)
      await refreshUser()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  if (!user) return null

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle className="mb-0">Account</SectionTitle>
        {!editing && (
          <Button variant="outline" size="sm" onClick={startEdit}>Edit</Button>
        )}
      </div>

      {editing ? (
        <form onSubmit={(e) => void handleSave(e)}>
          {error && <ErrorBox>{error}</ErrorBox>}

          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-semibold text-text-heading mb-1" htmlFor="fullName">
                Full name
              </label>
              <input
                id="fullName"
                className={inputClass(!fullName.trim() && !!error)}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="name"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-heading mb-1" htmlFor="jobTitle">
                Job title
              </label>
              <input
                id="jobTitle"
                className={inputClass(false)}
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="e.g. Senior Veterinarian"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-heading mb-1" htmlFor="phone">
                Phone number
              </label>
              <input
                id="phone"
                className={inputClass(false)}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                type="tel"
                autoComplete="tel"
                maxLength={30}
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button type="submit" size="sm" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </form>
      ) : (
        <dl className="grid gap-2 text-sm">
          <ProfileField label="Name" value={user.full_name} />
          <ProfileField label="Job title" value={user.job_title} />
          <ProfileField label="Phone" value={user.phone_number} />
          <ProfileField label="Email" value={user.email} />
          <ProfileField label="Role" value={ROLE_LABELS[user.role] ?? user.role} />
          <ProfileField label="Member since" value={formatDate(user.created_at)} />
        </dl>
      )}
    </Card>
  )
}

function ProfileField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex gap-2">
      <dt className="text-text-3 w-28 shrink-0">{label}</dt>
      <dd className="text-text">{value ?? <span className="text-text-3 italic">Not set</span>}</dd>
    </div>
  )
}

// ── Change password section ───────────────────────────────────────────────────

function ChangePasswordCard() {
  const toast = useToast()
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    if (!currentPw) { setError('Please enter your current password'); return }
    if (newPw.length < 8) { setError('New password must be at least 8 characters'); return }
    if (newPw !== confirmPw) { setError('New passwords do not match'); return }
    setSaving(true)
    try {
      await changePassword({ current_password: currentPw, new_password: newPw })
      toast.success('Password changed')
      setSuccess(true)
      setCurrentPw('')
      setNewPw('')
      setConfirmPw('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to change password')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <SectionTitle>Change Password</SectionTitle>

      {error && <ErrorBox>{error}</ErrorBox>}
      {success && (
        <div className="flex items-center gap-2 text-sm text-success-text bg-success-bg border border-success-border rounded px-3 py-2 mb-4">
          <CheckCircle size={14} strokeWidth={2.5} /> Password changed successfully.
        </div>
      )}

      <form onSubmit={(e) => void handleSubmit(e)} className="grid gap-4">
        <div>
          <label className="block text-sm font-semibold text-text-heading mb-1" htmlFor="currentPw">
            Current password
          </label>
          <input
            id="currentPw"
            type="password"
            className={inputClass(false)}
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            autoComplete="current-password"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-text-heading mb-1" htmlFor="newPw">
            New password
          </label>
          <input
            id="newPw"
            type="password"
            className={inputClass(false)}
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            autoComplete="new-password"
          />
          {newPw.length > 0 && newPw.length < 8 && (
            <p className="text-text-3 text-xs mt-1">{newPw.length}/8 characters minimum</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-semibold text-text-heading mb-1" htmlFor="confirmPw">
            Confirm new password
          </label>
          <input
            id="confirmPw"
            type="password"
            className={inputClass(false)}
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <div>
          <Button type="submit" size="sm" disabled={saving}>{saving ? 'Saving…' : 'Change password'}</Button>
        </div>
      </form>
    </Card>
  )
}

// ── Practice memberships section ──────────────────────────────────────────────

function PracticeMembershipCard() {
  const [applications, setApplications] = useState<StaffApplication[]>([])
  const [loading, setLoading] = useState(true)
  const [showApply, setShowApply] = useState(false)

  const load = () => {
    setLoading(true)
    myApplications()
      .then(setApplications)
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const { user } = useAuth()

  return (
    <>
      <Card>
        <div className="flex items-center justify-between mb-4">
          <SectionTitle className="mb-0">Practice Membership</SectionTitle>
          <Button variant="outline" size="sm" onClick={() => setShowApply(true)}>
            Apply to join a practice
          </Button>
        </div>

        {user?.role === 'user' && (
          <p className="text-sm text-text-3 mb-4">
            You don't have staff access yet. Apply to join a practice to get started.
          </p>
        )}

        {loading ? (
          <p className="text-sm text-text-3">Loading…</p>
        ) : applications.length === 0 ? (
          <p className="text-sm text-text-3">No practice memberships or applications yet.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {applications.map(a => (
              <div key={a.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div>
                  <div className="text-sm font-medium text-text-heading">{a.practice_name}</div>
                  <div className="text-xs text-text-3">{CLINICAL_ROLE_LABELS[a.clinical_role] ?? a.clinical_role}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[a.status] ?? ''}`}>
                  {STATUS_LABEL[a.status] ?? a.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {showApply && (
        <ApplyForStaff
          onClose={() => setShowApply(false)}
          onSuccess={() => { setShowApply(false); load() }}
        />
      )}
    </>
  )
}

// ── Digital signature section ─────────────────────────────────────────────────

function SignatureCard() {
  const { user, refreshUser } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const padRef = useRef<SignaturePad | null>(null)
  const [drawing, setDrawing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (drawing && canvasRef.current) {
      padRef.current = new SignaturePad(canvasRef.current, { backgroundColor: 'white' })
      return () => { padRef.current?.off(); padRef.current = null }
    }
  }, [drawing])

  const handleSave = async () => {
    if (!padRef.current || padRef.current.isEmpty()) { toast.error('Please draw your signature first'); return }
    setSaving(true)
    setError(null)
    try {
      await saveSignature(padRef.current.toDataURL('image/png'))
      toast.success('Signature saved')
      setDrawing(false)
      await refreshUser()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save signature')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    const ok = await confirm({ message: 'Remove your saved signature?', confirmLabel: 'Remove', danger: true })
    if (!ok) return
    setDeleting(true)
    setError(null)
    try {
      await deleteSignature()
      toast.success('Signature removed')
      await refreshUser()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove signature')
    } finally {
      setDeleting(false)
    }
  }

  if (!user) return null

  return (
    <Card>
      <SectionTitle>Digital Signature</SectionTitle>
      <p className="text-sm text-text-3 mb-4">
        Your signature is automatically attached when you dispense medication or record stock movements.
      </p>

      {error && <ErrorBox>{error}</ErrorBox>}

      {user.signature_data && !drawing && (
        <div className="mb-4">
          <div className="text-[0.8rem] text-text-3 mb-2">Current signature:</div>
          <img src={user.signature_data} alt="Your signature" className="border border-border rounded-lg max-w-full bg-surface" />
          <div className="flex gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={() => setDrawing(true)}>Redraw</Button>
            <Button variant="danger" size="sm" onClick={() => void handleDelete()} disabled={deleting}>
              {deleting ? 'Removing…' : 'Remove'}
            </Button>
          </div>
        </div>
      )}

      {!user.signature_data && !drawing && (
        <Button variant="primary" size="sm" onClick={() => setDrawing(true)}>Draw signature</Button>
      )}

      {drawing && (
        <div>
          <div className="text-[0.8rem] text-text-3 mb-2">Draw your signature below:</div>
          <canvas ref={canvasRef} width={480} height={150} className="border border-border-input rounded-lg block w-full touch-none" />
          <div className="flex gap-2 mt-3">
            <Button variant="primary" size="sm" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving…' : 'Save signature'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => padRef.current?.clear()} type="button">Clear</Button>
            <Button variant="ghost" size="sm" onClick={() => setDrawing(false)} type="button">Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function UserProfile() {
  const { user } = useAuth()
  if (!user) return null

  return (
    <div className="max-w-[580px] mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-text-heading mb-6">My Profile</h1>
      <div className="flex flex-col gap-6">
        <AccountCard />
        <ChangePasswordCard />
        <PracticeMembershipCard />
        <SignatureCard />
      </div>
    </div>
  )
}
